import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  ProductCatalogItem,
  ProductCatalogResponse,
  ProductCatalogSingleResponse,
  ProductCatalogRequest,
  ProductCatalogImportPreviewResponse,
  ProductCatalogImportCommitResponse,
} from '@/types/productCatalog';
import {
  fetchProductCatalog,
  searchProductCatalog,
  createProduct,
  updateProduct,
  deleteProduct,
  previewProductImport,
  commitProductImport,
} from '@/api/productCatalogService';
import { useProductCatalogStore } from '@/stores/productCatalogStore';
import { ErrorMessageResponse } from '@/types/error';
import { t } from 'i18next';

// Fetch products (paginated)
export const useFetchProductCatalog = (
  page = 1,
  perPage = 20,
  search?: string,
  category?: string,
) => {
  const setProducts = useProductCatalogStore((state) => state.setProducts);

  return useSentryQuery<ProductCatalogResponse, Error>(
    ['product-catalog', page, perPage, search, category],
    () => fetchProductCatalog(page, perPage, search, category),
    {
      onSuccess: (data) => {
        setProducts(data);
      },
      keepPreviousData: true,
    },
  );
};

// Search products (autocomplete)
export const useSearchProductCatalog = (search: string, enabled = true) => {
  return useSentryQuery<{ status: string; object: ProductCatalogItem[] }, Error>(
    ['product-catalog-search', search],
    () => searchProductCatalog(search),
    {
      enabled: enabled && search.length >= 1,
      keepPreviousData: true,
    },
  );
};

// Create product
export const useCreateProduct = () => {
  return useSentryMutation<ProductCatalogSingleResponse, ErrorMessageResponse, ProductCatalogRequest>(
    (data: ProductCatalogRequest) => createProduct(data),
    {
      defaultErrorMessage: t('failed_to_add_product', { defaultValue: 'Failed to add product' }),
    },
  );
};

// Update product
export const useUpdateProduct = (uid: string) => {
  return useSentryMutation<ProductCatalogSingleResponse, Error, Partial<ProductCatalogRequest>>(
    (data) => updateProduct(uid, data),
  );
};

// Delete product
export const useDeleteProduct = () => {
  return useSentryMutation<void, ErrorMessageResponse, string>(
    (uid: string) => deleteProduct(uid),
    {
      defaultErrorMessage: t('failed_to_remove_product', { defaultValue: 'Failed to remove product' }),
    },
  );
};

// Preview product import from CSV
export const usePreviewProductImport = () => {
  return useSentryMutation<ProductCatalogImportPreviewResponse, ErrorMessageResponse, File>(
    (file: File) => previewProductImport(file),
  );
};

// Commit product import from CSV
export const useCommitProductImport = () => {
  return useSentryMutation<ProductCatalogImportCommitResponse, ErrorMessageResponse, File>(
    (file: File) => commitProductImport(file),
  );
};
